import { useEffect, useState } from "react";
import { useAuth } from "@repo/ping-react-js";
import { Plus, ShieldCheck, Pencil, Trash2 } from "lucide-react";
import {
  fetchGlobalProducts,
  fetchGlobalCreds,
  setGlobalCreds,
  deleteGlobalCreds,
  type GlobalCredEntry,
} from "../../api/credentials";

type ModalState =
  | { type: "none" }
  | { type: "edit"; name: string; keys: string[] }
  | { type: "add" };

const GlobalSettingsPage = () => {
  const { accessToken } = useAuth();
  const [products, setProducts] = useState<string[]>([]);
  const [product, setProduct] = useState<string>("");
  const [creds, setCreds] = useState<GlobalCredEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: "none" });

  useEffect(() => {
    if (!accessToken) return;
    fetchGlobalProducts(accessToken)
      .then((res) => {
        setProducts(res.products);
        setProduct((cur) => cur || res.products[0] || "");
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load products"));
  }, [accessToken]);

  const load = async () => {
    if (!accessToken || !product) return;
    setLoading(true);
    setError(null);
    setWarning(null);
    try {
      const result = await fetchGlobalCreds(accessToken, product);
      setCreds(result.credentials);
      setWarning(result.warning ?? null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load credentials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [accessToken, product]);

  const handleSave = async (name: string, fields: Record<string, string>) => {
    if (!accessToken || !product) return;
    setSaving(name || "__new__");
    setSaveError(null);
    try {
      await setGlobalCreds(accessToken, product, name, fields);
      await load();
      setModal({ type: "none" });
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Failed to save credentials");
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (name: string) => {
    if (!accessToken || !product) return;
    setSaving(name);
    setSaveError(null);
    try {
      await deleteGlobalCreds(accessToken, product, name);
      await load();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Failed to remove credentials");
    } finally {
      setSaving(null);
    }
  };

  const configured = [...creds].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <header className="app-header">
        <div className="creds-header">
          <h1>Global Default Credentials</h1>
          <button
            className="apikeys-create-btn"
            disabled={!product}
            onClick={() => {
              setSaveError(null);
              setModal({ type: "add" });
            }}
          >
            <Plus size={16} />
            Add Integration
          </button>
        </div>
      </header>
      {products.length > 0 && (
        <div className="org-settings-tabs" role="tablist" aria-label="Product">
          {products.map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={p === product}
              className={`org-settings-tab ${p === product ? "active" : ""}`}
              onClick={() => setProduct(p)}
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <div className="app-content">
        {loading && <p className="creds-status">Loading...</p>}
        {error && <p className="creds-status creds-error">{error}</p>}
        {!loading && warning && <p className="creds-status creds-error">{warning}</p>}

        {!loading && !error && (
          <>
            {configured.length === 0 ? (
              <p className="creds-status">
                No global credentials configured. These serve as the default
                fallback for all organizations.
              </p>
            ) : (
              <div className="creds-grid">
                {configured.map((entry) => (
                  <div key={entry.name} className="creds-card has-creds">
                    <div className="creds-card-header">
                      <div className="creds-card-name" title={entry.name}>
                        <strong>{entry.name}</strong>
                      </div>
                      <div className="creds-card-actions">
                        <button
                          className="creds-edit-btn"
                          title="Edit"
                          onClick={() => {
                            setSaveError(null);
                            setModal({ type: "edit", name: entry.name, keys: entry.keys });
                          }}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className="creds-delete-btn"
                          title="Remove"
                          onClick={() => handleDelete(entry.name)}
                          disabled={saving === entry.name}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="creds-card-fields">
                      {entry.keys.map((k) => (
                        <div key={k} className="creds-field-row">
                          <span className="creds-field-key">{k}</span>
                          <span className="creds-field-value">{entry.value_hints?.[k] ?? '••••••••'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="creds-info">
              <ShieldCheck size={14} />
              <span>
                These credentials are used as the default fallback for all
                organizations when no org-level or user-level credentials are
                configured.
              </span>
            </div>
          </>
        )}
      </div>

      {(modal.type === "edit" || modal.type === "add") && (
        <GlobalCredentialEditModal
          existingName={modal.type === "edit" ? modal.name : ""}
          existingKeys={modal.type === "edit" ? modal.keys : []}
          existingNames={configured.map((c) => c.name)}
          saving={saving === (modal.type === "edit" ? modal.name : "__new__")}
          saveError={saveError}
          onSave={handleSave}
          onClose={() => setModal({ type: "none" })}
        />
      )}
    </>
  );
};

type FieldRow = { key: string; value: string };

const GlobalCredentialEditModal = ({
  existingName,
  existingKeys,
  existingNames,
  saving,
  saveError,
  onSave,
  onClose,
}: {
  existingName: string;
  existingKeys: string[];
  existingNames: string[];
  saving: boolean;
  saveError: string | null;
  onSave: (name: string, fields: Record<string, string>) => Promise<void>;
  onClose: () => void;
}) => {
  const isEdit = existingName !== "";
  const [name, setName] = useState(existingName);
  const [rows, setRows] = useState<FieldRow[]>(() =>
    isEdit && existingKeys.length > 0
      ? existingKeys.map((k) => ({ key: k, value: "" }))
      : [{ key: "", value: "" }],
  );

  const nameError =
    !isEdit && name.trim() !== "" && existingNames.includes(name.trim().toLowerCase())
      ? "An integration with this name already exists."
      : null;

  const handleSave = () => {
    const targetName = isEdit ? existingName : name.trim().toLowerCase();
    if (!targetName || nameError) return;
    const entries: Record<string, string> = {};
    for (const r of rows) {
      if (r.key.trim() && r.value) entries[r.key.trim().toUpperCase()] = r.value;
    }
    if (Object.keys(entries).length === 0) return;
    onSave(targetName, entries);
  };

  const canSave =
    !saving &&
    (isEdit || (name.trim() !== "" && !nameError)) &&
    rows.some((r) => r.key.trim() && r.value);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-dialog-integrations" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{isEdit ? `Edit ${existingName}` : "Add Integration"}</h2>

        {!isEdit && (
          <div className="modal-field">
            <label className="modal-label">Integration name</label>
            <input
              className="modal-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              placeholder="e.g. hazardhub"
              autoFocus
            />
            {nameError && <p className="creds-status creds-error">{nameError}</p>}
          </div>
        )}

        <div className="creds-edit-fields">
          <label className="modal-label">Settings keys</label>
          {rows.map((row, i) => (
            <div key={i} className="creds-edit-field-row">
              <input
                className="modal-input creds-edit-key creds-custom-key"
                type="text"
                value={row.key}
                onChange={(e) =>
                  setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, key: e.target.value.toUpperCase() } : r)))
                }
                placeholder="e.g. HAZARDHUB_API_KEY"
              />
              <input
                className="modal-input creds-edit-value"
                type="text"
                value={row.value}
                onChange={(e) =>
                  setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)))
                }
                placeholder={isEdit ? "*********" : "Value"}
              />
            </div>
          ))}
          <button className="modal-btn" onClick={() => setRows((prev) => [...prev, { key: "", value: "" }])}>
            + Add key
          </button>
        </div>

        {saveError && <p className="creds-status creds-error">{saveError}</p>}
        <div className="modal-actions">
          <button className="modal-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="modal-btn primary" onClick={handleSave} disabled={!canSave}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalSettingsPage;
